import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import api from '../services/api';

const IDE = () => {
  // Basic state
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('javascript');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  
  // Code Quality
  const [showCodeQuality, setShowCodeQuality] = useState(false);
  const [codeQualityResults, setCodeQualityResults] = useState(null);
  const [isAnalyzingCode, setIsAnalyzingCode] = useState(false);
  
  // IDE settings
  const [theme, setTheme] = useState('vs-dark');
  const [fontSize] = useState(14);
  const [showLineNumbers] = useState(true);
  const [wordWrap] = useState('on');
  const [showMinimap] = useState(false);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  
  const editorRef = useRef(null);
  
  // Handle window resize for mobile detection - throttled for performance
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      // Throttle resize events to prevent excessive updates
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth <= 640;
        setIsMobile(mobile);
      }, 150); // Throttle to 150ms
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Basic language templates for IDE navbar - memoized to prevent recreation on every render
  const basicTemplates = useMemo(() => ({
    javascript: `// JavaScript Basic Template
function main() {
    console.log("Hello, World!");
    
    // Your code here
    
    return "Program completed successfully!";
}

// Run the main function
const result = main();
console.log(result);`,

    python: `# Python Basic Template
def main():
    print("Hello, World!")
    
    # Your code here
    
    return "Program completed successfully!"

# Run the main function
if __name__ == "__main__":
    result = main()
    print(result)`,

    java: `// Java Basic Template
public class Main {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        
        // Your code here
        
        String result = "Program completed successfully!";
        System.out.println(result);
    }
}`,

    cpp: `// C++ Basic Template
#include <iostream>
using namespace std;

int main() {
    cout << "Hello, World!" << endl;
    
    // Your code here
    
    cout << "Program completed successfully!" << endl;
    return 0;
}`,

    c: `// C Basic Template
#include <stdio.h>

int main() {
    printf("Hello, World!\\n");
    
    // Your code here
    
    printf("Program completed successfully!\\n");
    return 0;
}`,

    csharp: `// C# Basic Template
using System;

class Program {
    static void Main(string[] args) {
        Console.WriteLine("Hello, World!");
        
        // Your code here
        
        Console.WriteLine("Program completed successfully!");
    }
}`,

      php: `<?php
// PHP Basic Template
function main() {
    echo "Hello, World!\\n";

    // Your code here
    
    return "Program completed successfully!";
}

// Run the main function
$result = main();
echo $result . "\\n";
?>`
  }), []);

  // Initialize IDE with basic template
  useEffect(() => {
    setCode(basicTemplates[language]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Handle language change - memoized
  const handleLanguageChange = useCallback((newLanguage) => {
    setLanguage(newLanguage);
    setCode(basicTemplates[newLanguage]);
  }, [basicTemplates]);

  // Run code
  const runCode = async () => {
    try {
      setIsRunning(true);
      setOutput('Running code...');
      
      const response = await api.post('/api/ide/execute', {
        code: code,
        language: language
      });
      
      setOutput(response.data.output);
    } catch (error) {
      setOutput(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  // Reset code
  const resetCode = () => {
    setCode(basicTemplates[language]);
    setOutput('♻️ Code reset to basic template');
  };

  // Check code quality
  const checkCodeQuality = async () => {
    if (!code.trim()) {
      setOutput('⚠️ Please write some code first');
      return;
    }

    setIsAnalyzingCode(true);
    setOutput('🔍 Analyzing code quality...');

    try {
      const response = await api.post('/api/code-quality/analyze', {
        code,
        language
      });

      setCodeQualityResults(response.data);
      setShowCodeQuality(true); // Open the modal
      setOutput(`✅ Code analysis complete! Score: ${response.data.score}/100`);
    } catch (error) {
      console.error('Code quality check failed:', error);
      setOutput(`❌ Analysis failed: ${error.response?.data?.message || error.message}`);
      setCodeQualityResults(null);
    } finally {
      setIsAnalyzingCode(false);
    }
  };

  // Editor options - optimized for performance
  const editorOptions = useMemo(() => ({
    minimap: { enabled: showMinimap },
    fontSize: fontSize,
    wordWrap: wordWrap,
    lineNumbers: showLineNumbers ? 'on' : 'off',
    automaticLayout: true,
    // Performance optimizations for smooth scrolling
    scrollBeyondLastLine: false,
    smoothScrolling: true,
    cursorSmoothCaretAnimation: 'on',
    renderWhitespace: 'none',
    renderLineHighlight: 'none',
    occurrencesHighlight: false,
    selectionHighlight: false,
    // Optimize rendering
    renderIndentGuides: false,
    renderValidationDecorations: 'off',
    // Scrollbar optimizations
    scrollbar: {
      useShadows: false,
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
      alwaysConsumeMouseWheel: false,
      verticalHasArrows: false,
      horizontalHasArrows: false,
    },
    // Disable features that can cause lag
    quickSuggestions: false,
    suggestOnTriggerCharacters: false,
    acceptSuggestionOnEnter: 'off',
    tabCompletion: 'off',
    wordBasedSuggestions: false,
    // Optimize for large files
    largeFileOptimizations: true,
    // Disable unnecessary features
    links: false,
    colorDecorators: false,
    folding: true,
    foldingStrategy: 'indentation',
    showFoldingControls: 'mouseover',
  }), [showMinimap, fontSize, wordWrap, showLineNumbers]);

  return (
    <div className="min-h-screen bg-gray-900 text-white" style={{ paddingTop: isMobile ? '60px' : '80px' }}>
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-3 sm:px-6 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <h1 className="text-lg sm:text-xl font-bold text-white">🚀 YCode IDE</h1>
            <span className="text-gray-400 text-xs sm:text-sm">Basic Coding Environment</span>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
            {/* Language Selector */}
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="bg-gray-700 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded border border-gray-600 text-xs sm:text-sm min-h-[44px] sm:min-h-0 flex-1 sm:flex-initial"
            >
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
              <option value="c">C</option>
              <option value="csharp">C#</option>
              <option value="php">PHP</option>
            </select>
            
            {/* Theme Toggle */}
            <button
              onClick={() => setTheme(theme === 'vs-dark' ? 'vs-light' : 'vs-dark')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
            >
              {theme === 'vs-dark' ? '🌙' : '☀️'}
            </button>

            {/* Reset Code */}
            <button
              onClick={resetCode}
              className="bg-gray-700 hover:bg-gray-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
            >
              <span className="hidden sm:inline">♻️ Reset</span>
              <span className="sm:hidden">♻️</span>
            </button>
            
            {/* Run Button */}
            <button
              onClick={runCode}
              disabled={isRunning}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded font-medium transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0 flex-1 sm:flex-initial"
            >
              {isRunning ? 'Running...' : '▶ Run'}
            </button>
            
            {/* Check Quality Button - Hidden on mobile */}
            {!isMobile && (
              <button
                onClick={checkCodeQuality}
                disabled={isRunning || isAnalyzingCode}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded font-medium transition-colors text-xs sm:text-sm"
              >
                {isAnalyzingCode ? 'Analyzing...' : '🔍 Check Quality'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className={`flex-1 flex ${isMobile ? 'flex-col' : ''} overflow-hidden`} 
        style={{ 
          height: isMobile ? 'auto' : 'calc(100vh - 9rem)', 
          minHeight: isMobile ? 'calc(100vh - 140px)' : 'auto',
          willChange: 'scroll-position',
          transform: 'translateZ(0)', // GPU acceleration for smooth scrolling
        }}
      >
        {/* Welcome Panel - Hidden on mobile */}
        {!isMobile && (
          <div className="w-96 bg-gray-800 border-r border-gray-700 overflow-y-auto">
            <div className="p-4 sm:p-6">
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🚀</div>
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Welcome to YCode IDE!
                  </h1>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                This is a clean, simple coding environment for practicing and learning programming languages.
              </p>
                </div>
                
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-900 p-3 sm:p-4 rounded-lg border border-gray-700">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm sm:text-base">
                  🌟 Features
                </h3>
                <ul className="text-gray-300 text-xs sm:text-sm space-y-1">
                  <li>• Multiple programming languages</li>
                  <li>• Real-time code execution</li>
                  <li>• Code quality analysis 🔍</li>
                  <li>• Syntax highlighting</li>
                  <li>• Dark/Light themes</li>
                  <li>• Clean, distraction-free interface</li>
                </ul>
                </div>

              <div className="bg-gray-900 p-3 sm:p-4 rounded-lg border border-gray-700">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm sm:text-base">
                  🎯 Getting Started
                </h3>
                <ul className="text-gray-300 text-xs sm:text-sm space-y-1">
                  <li>• Select your language</li>
                  <li>• Write or modify code</li>
                  <li>• Click Run to execute</li>
                  <li>• Click Check Quality for analysis</li>
                  <li>• View output below</li>
                </ul>
              </div>

              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-400 text-base sm:text-lg">💡</span>
                  <span className="text-blue-300 font-semibold text-xs sm:text-sm">Need Problem-Specific IDE?</span>
                </div>
                <p className="text-blue-200 text-xs sm:text-sm">
                  For solving coding problems with test cases and submissions, use the "Practice on IDE" button from any question page.
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Editor Section */}
        <div 
          className={`flex-1 flex flex-col ${isMobile ? 'order-1' : ''}`} 
          style={{ 
            minHeight: isMobile ? '400px' : 0,
            willChange: 'contents',
            transform: 'translateZ(0)', // GPU acceleration
          }}
        >
          {/* Editor */}
          <div 
            className="flex-1" 
            style={{ 
              minHeight: isMobile ? '400px' : 0,
              willChange: 'contents',
              transform: 'translateZ(0)', // GPU acceleration
            }}
          >
            <Editor
              height={isMobile ? '400px' : '100%'}
              language={language}
              value={code}
              onChange={setCode}
              theme={theme}
              options={editorOptions}
              loading={<div className="flex items-center justify-center h-full text-gray-400">Loading editor...</div>}
              onMount={(editor) => {
                editorRef.current = editor;
                // Additional performance optimizations after mount
                editor.updateOptions({
                  scrollbar: {
                    useShadows: false,
                    verticalScrollbarSize: 10,
                    horizontalScrollbarSize: 10,
                  },
                });
              }}
              beforeMount={(monaco) => {
                // Disable semantic validation for better performance
                monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                  noSemanticValidation: true,
                  noSyntaxValidation: false,
                });
              }}
            />
          </div>

          {/* Output Panel */}
          <div className="bg-gray-800 border-t border-gray-700 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-semibold text-white">Output</h3>
              <button onClick={() => setOutput('')} className="text-gray-400 hover:text-white text-xs min-h-[44px] sm:min-h-0 px-2 sm:px-0">Clear</button>
            </div>
            <div 
              className="bg-gray-900 p-2 sm:p-3 rounded" 
              style={{ 
                height: isMobile ? '200px' : '128px', 
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                willChange: 'scroll-position',
                transform: 'translateZ(0)', // GPU acceleration
              }}
            >
              <pre 
                className="whitespace-pre-wrap text-gray-300 text-xs sm:text-sm break-words"
                style={{ 
                  willChange: 'contents',
                  transform: 'translateZ(0)', // GPU acceleration
                }}
              >
                {output || '// Output will appear here'}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Code Quality Modal */}
      {showCodeQuality && codeQualityResults && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setShowCodeQuality(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-2xl border border-yellow-300 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 flex-shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500 rounded-full flex items-center justify-center text-xl sm:text-2xl">
                    🔍
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-gray-800">Code Quality Report</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">{codeQualityResults.rating || 'Analysis Complete'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end space-x-4">
                  <div className="text-center">
                    <div className={`text-2xl sm:text-4xl font-bold ${
                      codeQualityResults.score > 80 ? 'text-green-600' :
                      codeQualityResults.score > 60 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {Math.round(codeQualityResults.score)}
                    </div>
                    <div className="text-xs text-gray-500 font-medium">out of 100</div>
                  </div>
                  <button
                    onClick={() => setShowCodeQuality(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold hover:bg-yellow-100 rounded-full w-10 h-10 flex items-center justify-center transition-all min-h-[44px] sm:min-h-0"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              {codeQualityResults.smells && codeQualityResults.smells.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                    <span className="mr-2">⚠️</span>
                    Issues Found ({codeQualityResults.smells.length})
                  </h4>

                  {codeQualityResults.smells.map((smell, idx) => (
                    <div
                      key={idx}
                      className={`p-3 sm:p-4 rounded-lg border-l-4 shadow-sm ${
                        smell.severity === 'critical' ? 'bg-red-50 border-red-500' :
                        smell.severity === 'major' ? 'bg-orange-50 border-orange-500' :
                        smell.severity === 'minor' ? 'bg-yellow-50 border-yellow-500' :
                        'bg-blue-50 border-blue-500'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2 flex-wrap">
                            <span className={`px-2 py-1 text-xs font-bold rounded uppercase ${
                              smell.severity === 'critical' ? 'bg-red-200 text-red-800' :
                              smell.severity === 'major' ? 'bg-orange-200 text-orange-800' :
                              smell.severity === 'minor' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {smell.severity}
                            </span>
                            <h5 className="font-bold text-gray-800 text-sm sm:text-base break-words">{smell.type}</h5>
                          </div>

                          <p className="text-gray-700 mb-2 text-xs sm:text-sm break-words">{smell.description}</p>

                          {smell.fix && (
                            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                              <div className="text-xs font-semibold text-gray-600 mb-1">💡 How to fix:</div>
                              <div className="text-xs sm:text-sm text-gray-700 break-words">{smell.fix}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">✨</div>
                  <h4 className="text-xl sm:text-2xl font-bold text-green-600 mb-2">Excellent Code!</h4>
                  <p className="text-gray-600 text-sm sm:text-base">No code smells detected. Keep up the great work!</p>
                  <div className="mt-4 sm:mt-6 inline-flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-green-600 font-semibold text-sm sm:text-base">Score: {Math.round(codeQualityResults.score)}/100</span>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {codeQualityResults.suggestions && codeQualityResults.suggestions.length > 0 && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                    <span className="mr-2">💡</span>
                    Quick Suggestions
                  </h4>
                  <ul className="space-y-2">
                    {codeQualityResults.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-xs sm:text-sm text-gray-700">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span className="break-words">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDE; 
